import { z } from 'zod'
import { createdAtSortQueryShape, creatorListQueryShape } from '../list-query/contract.ts'
import { businessLicenseOcrResultSchema, idCardOcrResultSchema, ocrFileSchema } from '../ocr/contract.ts'
import { accountDisplaySchema } from '../profile/contract.ts'

const id = z.number().int().positive()
const version = z.number().int().positive()
export const documentChoiceSchema = z.discriminatedUnion('mode', [
  z.strictObject({ mode: z.literal('none') }),
  z.strictObject({ mode: z.literal('existing'), id, expectedUpdatedAt: z.iso.datetime() }),
  z.strictObject({
    mode: z.literal('registry'),
    creditCode: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[0-9A-Z]{18}$/),
    expectedDigest: z.string().regex(/^[a-f0-9]{64}$/)
  }),
  z.strictObject({
    mode: z.literal('recognized'),
    evidenceToken: z.string().min(32).max(200),
    expectedDigest: z.string().regex(/^[a-f0-9]{64}$/)
  })
])
export const documentAmendmentSchema = z
  .strictObject({
    company: documentChoiceSchema.optional(),
    identity: documentChoiceSchema.optional(),
    confirmLegalRepresentative: z.boolean().default(false),
    // Changing a nonempty reference is an explicit operation, never inferred from OCR.
    replacementReason: z.string().trim().min(1).max(500).optional(),
    clearPrimaryIdentity: z.boolean().default(false)
  })
  .refine(
    (value) =>
      !value.clearPrimaryIdentity ||
      (Boolean(value.replacementReason) && (!value.identity || value.identity.mode === 'none')),
    {
      message: '解除身份选用必须填写原因，且不能同时选择新身份'
    }
  )
export const serviceEnterpriseDraftSchema = z
  .strictObject({
    customerId: id,
    kind: z.enum(['prospectiveCompany', 'registeredCompany']),
    displayName: z.string().trim().min(1, '请输入内部称呼').max(255, '内部称呼不能超过 255 个字符'),
    note: z.string().trim().max(5000).nullable().optional(),
    documents: documentAmendmentSchema.optional()
  })
  .refine(
    (value) =>
      value.kind !== 'prospectiveCompany' || !value.documents?.company || value.documents.company.mode === 'none',
    {
      message: '已有营业执照时请选择已登记企业',
      path: ['kind']
    }
  )
export const enterpriseSelectionSchema = z.discriminatedUnion('mode', [
  z.strictObject({ mode: z.literal('none') }),
  z.strictObject({
    mode: z.literal('existing'),
    subjectId: id,
    expectedVersion: version,
    amendment: documentAmendmentSchema.optional()
  }),
  z.strictObject({ mode: z.literal('new'), draft: serviceEnterpriseDraftSchema })
])
export type DocumentChoice = z.infer<typeof documentChoiceSchema>
export type DocumentAmendment = z.infer<typeof documentAmendmentSchema>
export type ServiceEnterpriseDraft = z.infer<typeof serviceEnterpriseDraftSchema>
export type EnterpriseSelection = z.infer<typeof enterpriseSelectionSchema>

export const enterpriseOptionsSchema = z.strictObject({
  customerId: id,
  id: id.optional(),
  keyword: z.string().trim().max(100).optional(),
  current: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(100).default(20)
})
export const enterpriseDetailSchema = z.strictObject({ subjectId: id })
export const enterpriseListSchema = enterpriseOptionsSchema.omit({ customerId: true }).extend({
  customerId: id.optional(),
  kind: z.enum(['person', 'prospectiveCompany', 'registeredCompany']).optional(),
  isArchived: z.boolean().optional(),
  documentStatus: z
    .enum(['missingCompany', 'missingIdentity', 'missingLicenseOriginal', 'missingIdentityOriginal', 'complete'])
    .optional(),
  ...creatorListQueryShape,
  ...createdAtSortQueryShape
})
export const enterpriseCreateSchema = z.strictObject({ submissionKey: z.uuid(), draft: serviceEnterpriseDraftSchema })
export const enterpriseUpdateBasicsSchema = z.strictObject({
  subjectId: id,
  expectedVersion: version,
  displayName: z.string().trim().min(1).max(255),
  note: z.string().trim().max(5000).nullable().optional()
})
export const enterpriseSupplementDocumentsSchema = z.strictObject({
  submissionKey: z.uuid(),
  subjectId: id,
  expectedVersion: version,
  documents: documentAmendmentSchema,
  syncRentalOrderId: id.optional()
})
export const enterpriseBatchSetArchivedSchema = z
  .strictObject({
    items: z
      .array(z.strictObject({ subjectId: id, expectedVersion: version }))
      .min(1)
      .max(100),
    isArchived: z.boolean()
  })
  .refine((input) => new Set(input.items.map((item) => item.subjectId)).size === input.items.length, {
    message: '服务对象不能重复'
  })

export const enterpriseSummarySchema = z.object({
  id,
  customerId: id,
  kind: z.enum(['person', 'prospectiveCompany', 'registeredCompany']),
  customerName: z.string(),
  creator: accountDisplaySchema,
  displayName: z.string(),
  name: z.string(),
  note: z.string().nullable(),
  companyInfoId: id.nullable(),
  primaryIdentityId: id.nullable(),
  version,
  isArchived: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date(),
  company: z
    .object({
      id,
      name: z.string(),
      unifiedSocialCreditCode: z.string(),
      legalRepresentative: z.string().nullable(),
      legalRepresentativeIdentityId: id.nullable(),
      businessLicenseFileId: id.nullable(),
      updatedAt: z.date()
    })
    .nullable(),
  identity: z
    .object({
      id,
      name: z.string(),
      maskedIdNumber: z.string(),
      frontFileId: id.nullable(),
      backFileId: id.nullable(),
      updatedAt: z.date()
    })
    .nullable(),
  documentStatus: z.object({
    company: z.boolean(),
    identity: z.boolean(),
    businessLicense: z.boolean(),
    identityOriginal: z.boolean(),
    relationship: z.enum(['unlinked', 'consistent', 'needsReview'])
  })
})
export const enterpriseOptionsOutputSchema = z.object({
  data: enterpriseSummarySchema.array(),
  total: z.number(),
  current: z.number(),
  pageSize: z.number()
})
export const enterpriseListOutputSchema = enterpriseOptionsOutputSchema
export const enterpriseSupplementOutputSchema = z.object({
  subject: enterpriseSummarySchema,
  syncedRentalOrderId: id.nullable()
})
export const enterpriseBatchOutputSchema = z.object({ count: z.number().int().nonnegative() })
export const enterprisePreviewInputSchema: z.ZodType<{ file: File; kind: 'company' | 'identity' }, FormData> = z
  .instanceof(FormData)
  .transform((data) => ({ file: data.get('file'), kind: data.get('kind') }))
  .pipe(z.object({ file: ocrFileSchema, kind: z.enum(['company', 'identity']) }))
export const enterprisePreviewOutputSchema = z.object({
  kind: z.enum(['company', 'identity']),
  fileId: id,
  evidenceToken: z.string(),
  expectedDigest: z.string(),
  expiresAt: z.date(),
  result: z.union([businessLicenseOcrResultSchema, idCardOcrResultSchema.strip()])
})
export type EnterpriseSummary = z.infer<typeof enterpriseSummarySchema>
export type EnterpriseOptionsInput = z.infer<typeof enterpriseOptionsSchema>
export type EnterpriseListInput = z.infer<typeof enterpriseListSchema>
export type EnterpriseCreateInput = z.infer<typeof enterpriseCreateSchema>
export type EnterpriseUpdateBasicsInput = z.infer<typeof enterpriseUpdateBasicsSchema>
export type EnterpriseSupplementDocumentsInput = z.infer<typeof enterpriseSupplementDocumentsSchema>
export type EnterpriseBatchSetArchivedInput = z.infer<typeof enterpriseBatchSetArchivedSchema>

export const enterpriseRegistryLookupSchema = z.object({
  creditCode: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[0-9A-Z]{18}$/, '请输入 18 位统一社会信用代码')
})

export const rentalOrderSupplementSchema = z.object({
  rentalOrderId: id,
  expectedUpdatedAt: z.date(),
  enterpriseSelection: enterpriseSelectionSchema.refine((selection) => selection.mode !== 'none', '请选择服务对象'),
  idempotencyKey: z.uuid()
})
export type RentalOrderSupplementInput = z.infer<typeof rentalOrderSupplementSchema>
